import type {
  BaseErrorResponse,
  EmptySuccessResponse,
  GetWalletTransferRuleDestinationVMEntityRes,
  GetWalletTransferRuleVMEntityRes,
  UpdateWalletTransferRuleReq,
  WalletTransferRuleDestinationDto,
  WalletTransferRuleDestinationVMPaginationResponse,
  WalletTransferRuleDto,
  WalletTransferRuleVMPaginationResponse,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useWalletTransferRule() {
  class WalletTransferRule<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Wallet Transfer Rule Destination
     * @name WalletTransferRuleDestinationControllerDeleteEntity
     * @summary Wallet transfer rule destination delete
     * @request DELETE:/api3/wallet-transfer-rule/{transferRuleId}/destination/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleDestinationControllerDeleteEntity = (
      id: string,
      transferRuleId: string,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${transferRuleId}/destination/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule Destination
     * @name WalletTransferRuleDestinationControllerUpdateEntity
     * @summary Wallet transfer rule destination update
     * @request PUT:/api3/wallet-transfer-rule/{transferRuleId}/destination/{id}
     * @secure
     * @response `200` `GetWalletTransferRuleDestinationVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleDestinationControllerUpdateEntity = (
      id: string,
      transferRuleId: string,
      data: WalletTransferRuleDestinationDto,
      params: RequestParams = {},
    ) =>
      this.request<GetWalletTransferRuleDestinationVMEntityRes, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${transferRuleId}/destination/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule Destination
     * @name WalletTransferRuleDestinationControllerGetEntityByPk
     * @summary Get Wallet transfer rule destination by PK
     * @request GET:/api3/wallet-transfer-rule/{transferRuleId}/destination/{id}
     * @secure
     * @response `200` `GetWalletTransferRuleDestinationVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleDestinationControllerGetEntityByPk = (
      id: string,
      transferRuleId: string,
      params: RequestParams = {},
    ) =>
      this.request<GetWalletTransferRuleDestinationVMEntityRes, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${transferRuleId}/destination/${id}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule Destination
     * @name WalletTransferRuleDestinationControllerCreateEntity
     * @summary Wallet transfer rule destination create
     * @request POST:/api3/wallet-transfer-rule/{transferRuleId}/destination
     * @secure
     * @response `200` `GetWalletTransferRuleDestinationVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleDestinationControllerCreateEntity = (
      transferRuleId: string,
      data: WalletTransferRuleDestinationDto,
      params: RequestParams = {},
    ) =>
      this.request<GetWalletTransferRuleDestinationVMEntityRes, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${transferRuleId}/destination`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule Destination
     * @name WalletTransferRuleDestinationControllerGetAllPaginatedEntities
     * @summary Get Wallet transfer rule destinations
     * @request GET:/api3/wallet-transfer-rule/{transferRuleId}/destination
     * @secure
     * @response `200` `WalletTransferRuleDestinationVMPaginationResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleDestinationControllerGetAllPaginatedEntities = (
      transferRuleId: string,
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"wallet-transfer-rule-destination.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"wallet-transfer-rule-destination.accountId","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"wallet-transfer-rule-destination.id":"{ ASC | DESC }","wallet-transfer-rule-destination.accountId":"{ ASC | DESC }"}
         */
        orderBy?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<WalletTransferRuleDestinationVMPaginationResponse, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${transferRuleId}/destination`,
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule
     * @name WalletTransferRuleControllerDeleteEntity
     * @summary Wallet transfer rule delete
     * @request DELETE:/api3/wallet-transfer-rule/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleControllerDeleteEntity = (id: string, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule
     * @name WalletTransferRuleControllerUpdateEntity
     * @summary Wallet transfer rule update
     * @request PUT:/api3/wallet-transfer-rule/{id}
     * @secure
     * @response `200` `GetWalletTransferRuleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleControllerUpdateEntity = (
      id: string,
      data: UpdateWalletTransferRuleReq,
      params: RequestParams = {},
    ) =>
      this.request<GetWalletTransferRuleVMEntityRes, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule
     * @name WalletTransferRuleControllerGetEntityByPk
     * @summary Get Wallet transfer rule by PK
     * @request GET:/api3/wallet-transfer-rule/{id}
     * @secure
     * @response `200` `GetWalletTransferRuleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleControllerGetEntityByPk = (id: string, params: RequestParams = {}) =>
      this.request<GetWalletTransferRuleVMEntityRes, BaseErrorResponse>({
        path: `/api3/wallet-transfer-rule/${id}`,
        method: 'GET',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule
     * @name WalletTransferRuleControllerCreateEntity
     * @summary Wallet transfer rule create
     * @request POST:/api3/wallet-transfer-rule
     * @secure
     * @response `200` `GetWalletTransferRuleVMEntityRes`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleControllerCreateEntity = (data: WalletTransferRuleDto, params: RequestParams = {}) =>
      this.request<GetWalletTransferRuleVMEntityRes, BaseErrorResponse>({
        path: '/api3/wallet-transfer-rule',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Wallet Transfer Rule
     * @name WalletTransferRuleControllerGetAllPaginatedEntities
     * @summary Get Wallet transfer rules
     * @request GET:/api3/wallet-transfer-rule
     * @secure
     * @response `200` `WalletTransferRuleVMPaginationResponse`
     * @response `400` `BaseErrorResponse`
     */
    walletTransferRuleControllerGetAllPaginatedEntities = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
        /** @example [{"field":"wallet-transfer-rule.id","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null},{"field":"wallet-transfer-rule.ruleType","operation":"{ != | = | in | n-in | like | iLike | n-like | n-iLike }","value":null},{"field":"wallet-transfer-rule.accountId","operation":"{ != | = | > | >= | < | <= | between | n-between | in | n-in }","value":null}] */
        filter?: object;
        /**
         * Sort options
         * @example {"wallet-transfer-rule.id":"{ ASC | DESC }","wallet-transfer-rule.ruleType":"{ ASC | DESC }","wallet-transfer-rule.accountId":"{ ASC | DESC }"}
         */
        orderBy?: object;
      },
      params: RequestParams = {},
    ) =>
      this.request<WalletTransferRuleVMPaginationResponse, BaseErrorResponse>({
        path: '/api3/wallet-transfer-rule',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new WalletTransferRule();
}
